import React, { useEffect, useState } from "react";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import PrivateComponentLoadingPage from "../LoadingPage/PrivateComponentLoadingPage";
import PaymentsAppLayout from "../../Components/Molecules/PaymentsAppLayout";
import { useAdminStore } from "../../Store/Admin";
import { useParams } from "react-router-dom";
import LoadingPage from "../LoadingPage";
import { ReactComponent as InfoIcon } from "../../Assets/ActionIcons/InfoIcon.svg";
import { ReactComponent as ExternalLinkIcon } from "../../Assets/ActionIcons/ExternalLinkIcon.svg";
import { PaymentRoutes } from "../../Contracts/Routes";
import { AdminPortalDetails } from "../../Contracts/Admin";

const AdminTeamDetailPage: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const {
    isLoading,
    teamDetails,
    teamAdyenEntityDetails,
    adminPortalDetails,
    getOneTeam,
    getTeamAdyenEntityDetails,
    getAdminPortalActionDetails,
    editAdminPortalActionDetails
  } = useAdminStore();
  const [dirtyPortalDetails, setDirtyPortalDetails] = useState<Partial<AdminPortalDetails>>({});
  const handleChange = (key: keyof AdminPortalDetails, value: any) => {
    setDirtyPortalDetails(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const { team_id } = useParams();
  useEffect(() => {
    team_id &&
      Promise.all([
        getOneTeam(getAccessTokenSilently, team_id),
        getTeamAdyenEntityDetails(getAccessTokenSilently, team_id),
        getAdminPortalActionDetails(getAccessTokenSilently, team_id)
      ]);
  }, [
    team_id,
    getAccessTokenSilently,
    getOneTeam,
    getTeamAdyenEntityDetails,
    getAdminPortalActionDetails
  ]);

  const savePortalDetails = async () => {
    team_id &&
      (await editAdminPortalActionDetails(getAccessTokenSilently, team_id, dirtyPortalDetails));

    setDirtyPortalDetails({});
  };

  const updatePortalDetails = {
    ...adminPortalDetails,
    ...dirtyPortalDetails
  };

  const isDirty = Object.keys(dirtyPortalDetails).length > 0;
  return (
    <PaymentsAppLayout documentTitle={`${teamDetails?.name} | Admin`} noPadding>
      <div className="grid grid-cols-2 gap-6">
        <div className="p-6 border-r h-[calc(100vh-6rem)]">
          <div className="text-xl font-semibold">{teamDetails?.name}</div>
          <div>
            {teamDetails && !isLoading && (
              <>
                <div className="font-semibold mt-4">Summary</div>
                <table className="my-4">
                  <tbody className="border">
                    <tr className="even:bg-white odd:bg-gray-100 border-b">
                      <td className="px-4 py-2">Company Name</td>
                      <td className="px-4 py-2">{teamDetails.name}</td>
                    </tr>
                    <tr className="even:bg-white odd:bg-gray-100 border-b">
                      <td className="px-4 py-2">Zomentum Company Id</td>
                      <td className="px-4 py-2">{teamDetails.zomentum_company_id}</td>
                    </tr>
                    <tr className="even:bg-white odd:bg-gray-100 border-b">
                      <td className="px-4 py-2">Billing email</td>
                      <td className="px-4 py-2">{teamDetails.billing_email}</td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}
          </div>
          {isLoading ? (
            <div className="m-12">
              <LoadingPage />
            </div>
          ) : (
            <div className="flex mt-4 space-x-3">
              <div>
                <div className="font-semibold">Adyen Entities</div>
                <table className="my-4">
                  <tbody className="border">
                    <tr className="even:bg-white odd:bg-gray-100 border-b">
                      <td className="px-4 py-2">Legal Entity Name</td>
                      <td className="px-4 py-2">
                        {teamAdyenEntityDetails?.legal_entity_name || "NA"}
                      </td>
                    </tr>
                    <tr className="even:bg-white odd:bg-gray-100 border-b">
                      <td className="px-4 py-2">Legal Entity Type</td>
                      <td className="px-4 py-2 capitalize">
                        {teamAdyenEntityDetails?.legal_entity_type || "NA"}
                      </td>
                    </tr>
                    <tr className="even:bg-white odd:bg-gray-100 border-b">
                      <td className="px-4 py-2">Legal Entity ID</td>
                      <td className="px-4 py-2">
                        {teamAdyenEntityDetails?.legal_entity_id || "NA"}
                      </td>
                    </tr>
                    <tr className="even:bg-white odd:bg-gray-100 border-b">
                      <td className="px-4 py-2">Account Holder ID</td>
                      <td className="px-4 py-2">
                        {teamAdyenEntityDetails?.account_holder_id || "NA"}
                      </td>
                    </tr>
                    <tr className="even:bg-white odd:bg-gray-100 border-b">
                      <td className="px-4 py-2">Balance Account ID</td>
                      <td className="px-4 py-2">
                        {teamAdyenEntityDetails?.balance_account_id || "NA"}
                      </td>
                    </tr>
                    <tr className="even:bg-white odd:bg-gray-100 border-b">
                      <td className="px-4 py-2">Business Line ID</td>
                      <td className="px-4 py-2">
                        {teamAdyenEntityDetails?.business_line_id || "NA"}
                      </td>
                    </tr>
                    <tr className="even:bg-white odd:bg-gray-100 border-b">
                      <td className="px-4 py-2">Store ID</td>
                      <td className="px-4 py-2">{teamAdyenEntityDetails?.store_id || "NA"}</td>
                    </tr>
                    <tr className="even:bg-white odd:bg-gray-100 border-b">
                      <td className="px-4 py-2">Country</td>
                      <td className="px-4 py-2">{teamAdyenEntityDetails?.country || "NA"}</td>
                    </tr>
                    <tr className="even:bg-white odd:bg-gray-100 border-b">
                      <td className="px-4 py-2">Currency</td>
                      <td className="px-4 py-2">{teamAdyenEntityDetails?.currency || "NA"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div>
                <div className="font-semibold">Capabilities</div>

                <table className="my-4">
                  <tbody className="border">
                    <tr className="even:bg-white odd:bg-gray-100 border-b">
                      <td className="px-4 py-2">Receive Payments</td>
                      <td className="px-4 py-2 capitalize">
                        {teamAdyenEntityDetails?.receive_payment_capability || "NA"}
                      </td>
                    </tr>
                    <tr className="even:bg-white odd:bg-gray-100 border-b">
                      <td className="px-4 py-2">Payouts</td>
                      <td className="px-4 py-2 capitalize">
                        {teamAdyenEntityDetails?.payouts_capability || "NA"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <div className="p-6">
          <div className="font-semibold text-xl mb-6">Actions</div>
          <div className="grid grid-cols-6">
            <div className="col-span-4 grid grid-cols-2 gap-4">
              <label>Has access to Zomentum Payments?</label>
              <div className="flex space-x-2 col-span-1">
                <div
                  className="flex items-center space-x-2 cursor-pointer"
                  onClick={() => handleChange("zomentum_payments", true)}
                >
                  <input type="radio" checked={updatePortalDetails?.zomentum_payments} />
                  <div>Yes</div>
                </div>
                <div
                  className="flex items-center space-x-2 cursor-pointer"
                  onClick={() => handleChange("zomentum_payments", false)}
                >
                  <input type="radio" checked={!updatePortalDetails?.zomentum_payments} />
                  <div>No</div>
                </div>
              </div>
              <label>Direct debits are free until:</label>
              <input
                className="border rounded"
                type="date"
                value={updatePortalDetails?.direct_debit_free_until_date?.toString().split("T")[0]}
                onChange={e => handleChange("direct_debit_free_until_date", e.target.value)}
              />
              <label className="flex items-center space-x-2">
                <div>Custom transaction limit:</div>
                <div className="group relative">
                  <InfoIcon className="w-4 h-4 cursor-pointer" />
                  <div className="absolute text-xs left-5 bottom-[-1.5rem] z-50 ml-2 px-2 py-1 bg-gray-950 text-white rounded-lg shadow-lg text-nowrap group-hover:block hidden">
                    <div className="w-20 text-wrap">
                      A value of 0 means the default limit of 60,000 is used.
                    </div>
                    <svg
                      className="absolute left-[-8px] top-1/2 transform -translate-y-1/2"
                      width="8"
                      height="8"
                      viewBox="0 0 8 8"
                    >
                      <polygon points="0,4 8,0 8,8" />
                    </svg>
                  </div>
                </div>
              </label>
              <input
                className="border rounded"
                type="number"
                min={0}
                step="any"
                value={updatePortalDetails?.payment_maximum_transaction_amount}
                onChange={e =>
                  handleChange("payment_maximum_transaction_amount", parseFloat(e.target.value))
                }
              />
              <label>Payouts Paused?</label>
              <div className="flex space-x-2 col-span-1">
                <div
                  className="flex items-center space-x-2 cursor-pointer"
                  onClick={() => handleChange("pause_payouts", true)}
                >
                  <input type="radio" checked={updatePortalDetails?.pause_payouts} />
                  <div>Yes</div>
                </div>
                <div
                  className="flex items-center space-x-2 cursor-pointer"
                  onClick={() => handleChange("pause_payouts", false)}
                >
                  <input type="radio" checked={!updatePortalDetails?.pause_payouts} />
                  <div>No</div>
                </div>
              </div>
            </div>
            <div className="flex items-center px-8 col-span-2">
              <button
                className="bg-blue-500 px-4 py-2 border-blue-600 rounded text-white hover:scale-105"
                disabled={!isDirty}
                onClick={savePortalDetails}
              >
                Save
              </button>
              {isDirty && (
                <button
                  className="px-4 py-2 text-red-600 hover:scale-105"
                  disabled={!isDirty}
                  onClick={() => setDirtyPortalDetails({})}
                >
                  Discard
                </button>
              )}
            </div>
          </div>
          {team_id && (
            <div className="my-4 flex flex-col space-y-2">
              <a
                className="flex items-center cursor-pointer space-x-2 hover:text-blue-500 text-blue-600 underline col-span-2"
                href={PaymentRoutes.ADMIN_TEAM_TRANSFERS_LIST.replace(":team_id", team_id)}
              >
                <div>Initiate Payout</div>
                <ExternalLinkIcon className="h-3 w-3" />
              </a>

              <a
                className="flex items-center cursor-pointer space-x-2 hover:text-blue-500 text-blue-600 underline col-span-2"
                href={PaymentRoutes.ADMIN_TEAM_TRANSACTION_LIST.replace(":team_id", team_id)}
              >
                <div>View Transactions</div>
                <ExternalLinkIcon className="h-3 w-3" />
              </a>
              <a
                className="flex items-center cursor-pointer space-x-2 hover:text-blue-500 text-blue-600 underline col-span-2"
                href={PaymentRoutes.ADMIN_TEAM_USER_LIST.replace(":team_id", team_id)}
              >
                <div>View Users</div>
                <ExternalLinkIcon className="h-3 w-3" />
              </a>
              <a
                className="flex items-center cursor-pointer space-x-2 hover:text-blue-500 text-blue-600 underline col-span-2"
                href={PaymentRoutes.ADMIN_TEAM_PAYOUTS_LIST.replace(":team_id", team_id)}
              >
                <div>View Payouts</div>
                <ExternalLinkIcon className="h-3 w-3" />
              </a>
            </div>
          )}
        </div>
      </div>
    </PaymentsAppLayout>
  );
};

export default withAuthenticationRequired(AdminTeamDetailPage, {
  onRedirecting: () => <PrivateComponentLoadingPage />
});
